/* eslint-disable max-statements */
import { Box, Button, Grid, Group, LoadingOverlay, Stack, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { Declarant, TemplateType } from '@e-origin/shared';

import { LeavePageNotification, Select } from '@components';
import { useCustomersNamesAndIds } from '@hooks/customer';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useTemplates } from '@hooks/useTemplates';

const declarantSchema = z.object({
  defaultCustomersAndTemplates: z.object({
    highValues: z.object({
      customer: z.string().nullable().optional(),
      template: z.string().nullable().optional(),
    }),
    highValuesExport: z.object({
      customer: z.string().nullable().optional(),
      template: z.string().nullable().optional(),
    }),
    lowValues: z.object({
      b2cLv: z.object({
        customer: z.string().nullable().optional(),
        template: z.string().nullable().optional(),
      }),
      b2cHv: z.object({
        customer: z.string().nullable().optional(),
        template: z.string().nullable().optional(),
      }),
    }),
  }),
});

type DefaultCustomersAndTemplatesFormValues = z.infer<typeof declarantSchema>;

export const DefaultCustomersAndTemplatesForm = () => {
  const { data: declarant, isLoading } = useDeclarant();
  const { data: templatesList, isLoading: templatesLoading } = useTemplates();
  const customersNamesAndIds = useCustomersNamesAndIds();

  const updateDeclarantMutation = useUpdateDeclarant();

  const highValueTemplates = useMemo(() => {
    return (
      templatesList
        ?.filter((t) => t.type === TemplateType.HIGH_VALUE_H1 && !t.isExport && t.published !== false)
        .map((template) => ({
          label: template.name,
          value: template._id,
        })) || []
    );
  }, [templatesList]);

  const exportTemplates = useMemo(() => {
    return (
      templatesList
        ?.filter((t) => t.isExport && t.published !== false)
        .map((template) => ({
          label: template.name,
          value: template._id,
        })) || []
    );
  }, [templatesList]);

  const lowValueTemplates = useMemo(() => {
    return (
      templatesList
        ?.filter((t) => t.type === TemplateType.LOW_VALUE_H7 && t.published !== false)
        .map((template) => ({
          label: template.name,
          value: template._id,
        })) || []
    );
  }, [templatesList]);

  const getValidTemplate = (templateId: string, templateList: { label: string; value: string }[]) => {
    return templateList.some((t) => t.value === templateId) ? templateId : null;
  };

  const buildInitialValues = (d: Declarant): DefaultCustomersAndTemplatesFormValues => ({
    defaultCustomersAndTemplates: {
      highValues: {
        customer: d?.defaultCustomersAndTemplates?.highValues?.customer || null,
        template: getValidTemplate(d?.defaultCustomersAndTemplates?.highValues?.template || '', highValueTemplates),
      },
      highValuesExport: {
        customer: d?.defaultCustomersAndTemplates?.highValuesExport?.customer || null,
        template: getValidTemplate(d?.defaultCustomersAndTemplates?.highValuesExport?.template || '', exportTemplates),
      },
      lowValues: {
        b2cLv: {
          customer: d?.defaultCustomersAndTemplates?.lowValues?.b2cLv?.customer || null,
          template: getValidTemplate(d?.defaultCustomersAndTemplates?.lowValues?.b2cLv?.template || '', lowValueTemplates),
        },
        b2cHv: {
          customer: d?.defaultCustomersAndTemplates?.lowValues?.b2cHv?.customer || null,
          template: getValidTemplate(d?.defaultCustomersAndTemplates?.lowValues?.b2cHv?.template || '', highValueTemplates),
        },
      },
    },
  });

  const form = useFormWithNameInputProp<DefaultCustomersAndTemplatesFormValues>({
    initialValues: buildInitialValues(declarant),
    validateInputOnBlur: true,
    validate: zodResolver(declarantSchema),
  });

  const handleSubmit = (values: DefaultCustomersAndTemplatesFormValues) => {
    if (declarant) {
      updateDeclarantMutation.mutate({
        declarantId: declarant._id,
        declarant: values,
      });
    }
  };

  useEffect(() => {
    if (declarant) {
      form.setValues(buildInitialValues(declarant));
      form.resetDirty();
    }
  }, [declarant]);

  if (!declarant || templatesLoading) {
    return <LoadingOverlay visible={isLoading || templatesLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Box>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Box>
            <Title order={4} fw={600} mb="md">
              High Values
            </Title>
            <Grid>
              <Grid.Col span={6}>
                <Select
                  {...form.getInputProps(`defaultCustomersAndTemplates.highValues.customer`)}
                  label="Select customer"
                  data={customersNamesAndIds}
                  searchable
                  clearable
                  size="lg"
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  {...form.getInputProps(`defaultCustomersAndTemplates.highValues.template`)}
                  label="Select template"
                  data={highValueTemplates}
                  searchable
                  clearable
                  size="lg"
                />
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Title order={4} fw={600} mb="md">
              Export
            </Title>
            <Grid>
              <Grid.Col span={6}>
                <Select
                  {...form.getInputProps(`defaultCustomersAndTemplates.highValuesExport.customer`)}
                  label="Select customer"
                  data={customersNamesAndIds}
                  searchable
                  clearable
                  size="lg"
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  {...form.getInputProps(`defaultCustomersAndTemplates.highValuesExport.template`)}
                  label="Select template"
                  data={exportTemplates}
                  searchable
                  clearable
                  size="lg"
                />
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Title order={4} fw={600} mb="md">
              Low Values
            </Title>

            <Stack gap="md">
              <Box>
                <Title order={5} fw={500} mb="sm">
                  B2C LV
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Select
                      {...form.getInputProps(`defaultCustomersAndTemplates.lowValues.b2cLv.customer`)}
                      label="Select customer"
                      data={customersNamesAndIds}
                      searchable
                      clearable
                      size="lg"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select
                      {...form.getInputProps(`defaultCustomersAndTemplates.lowValues.b2cLv.template`)}
                      label="Select template"
                      data={lowValueTemplates}
                      searchable
                      clearable
                      size="lg"
                    />
                  </Grid.Col>
                </Grid>
              </Box>

              <Box>
                <Title order={5} fw={500} mb="sm">
                  B2C HV Splitting
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Select
                      {...form.getInputProps(`defaultCustomersAndTemplates.lowValues.b2cHv.customer`)}
                      label="Select customer"
                      data={customersNamesAndIds}
                      searchable
                      clearable
                      size="lg"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Select
                      {...form.getInputProps(`defaultCustomersAndTemplates.lowValues.b2cHv.template`)}
                      label="Select template"
                      data={highValueTemplates}
                      searchable
                      clearable
                      size="lg"
                    />
                  </Grid.Col>
                </Grid>
              </Box>
            </Stack>
          </Box>

          <Group py="xl">
            <Button type="submit" variant="filled" size="lg" loading={updateDeclarantMutation.isPending} disabled={!form.isValid() || !form.isDirty()}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};
